/**
 * UnifiedDateInput — the single date-entry component for the entire app.
 *
 * Features:
 *   - Typeable text input that parses the user's active date-format setting
 *   - Lenient parsing (dd/mmm/yyyy, dd/mm/yyyy, mmm d yyyy, yyyy-mm-dd, etc.)
 *   - Normalizes to the active display format on blur and on Enter
 *   - Calendar popover button on the right
 *   - "Today" quick-fill button, always visible
 *   - Enter commits the typed value
 *   - Tentative/greyed style when pre-filled with today's date as default
 *   - Inline validation: unparseable value → red hint (field NOT cleared)
 *   - Centralized 529 date validation via dateValidation.js
 *   - Always stores and emits ISO yyyy-mm-dd
 *
 * Props:
 *   value       — ISO yyyy-mm-dd string (or "")
 *   onChange(iso) — called with ISO string or ""
 *   testid      — data-testid for the text input
 *   disabled    — disables interaction
 *   className   — extra classes on the outer wrapper
 *   min / max   — ISO date bounds for the calendar
 *   tentativeToday — if true, renders today-default in greyed style until touched
 *   placeholder — optional override (defaults to format hint)
 *   hideToday   — hide the Today button (for recurrence-end etc.)
 *   preset      — "softFutureDate" (money dates: future warns, never blocks) | "recordDate" (general 529 date) | "completionDate" (forward-looking, e.g. graduation)
 *   assignedTaxYear — if set, warns on date↔year mismatch
 *   onValidation — optional callback({ error, warning }) for parent to block save
 */
import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { CalendarBlank, WarningCircle } from "@phosphor-icons/react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { fmtDate, getDateFormat, getDateFormatHint, parseDate, localISO } from "@/lib/format";
import { validateDate } from "@/lib/dateValidation";

function isoToDate(iso) {
  if (!iso) return undefined;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : undefined;
}
function dateToIso(d) {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const TODAY_ISO = localISO();

export function UnifiedDateInput({
  value,
  onChange,
  testid,
  disabled = false,
  className = "",
  min,
  max,
  tentativeToday = false,
  placeholder,
  hideToday = false,
  preset,
  assignedTaxYear,
  onValidation,
}) {
  const [calOpen, setCalOpen] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [warningAcked, setWarningAcked] = useState(false);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);

  const runValidation = useCallback((iso) => {
    if (!iso) {
      setError(""); setWarning(""); setWarningAcked(false);
      onValidation?.({ error: null, warning: null });
      return;
    }
    const v = validateDate(iso, { preset, assignedTaxYear });
    setError(v.error || "");
    setWarning(v.warning || "");
    if (v.warning !== warning) setWarningAcked(false);
    onValidation?.(v);
  }, [preset, assignedTaxYear, onValidation, warning]);

  // Sync text from external value changes (calendar pick, parent reset, initial load)
  const lastEmitted = useRef(value);
  useEffect(() => {
    // Only sync when value changed externally (not from our own typing)
    if (value !== lastEmitted.current || text === "") {
      lastEmitted.current = value;
      setText(value ? fmtDate(value, getDateFormat()) : "");
      setError("");
      runValidation(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Re-validate when assignedTaxYear changes (for date↔year mismatch)
  useEffect(() => {
    if (value) runValidation(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedTaxYear]);

  const isTentative = tentativeToday && value === TODAY_ISO && !touched;

  const commit = useCallback((raw) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      setError(""); setWarning(""); setWarningAcked(false);
      lastEmitted.current = "";
      onChange("");
      onValidation?.({ error: null, warning: null });
      return;
    }
    const iso = parseDate(trimmed);
    if (iso) {
      setError("");
      setText(fmtDate(iso, getDateFormat()));
      lastEmitted.current = iso;
      onChange(iso);
      setTouched(true);
      // Run validation on the parsed date
      const v = validateDate(iso, { preset, assignedTaxYear });
      setError(v.error || "");
      setWarning(v.warning || "");
      if (v.warning !== warning) setWarningAcked(false);
      onValidation?.(v);
    } else {
      setError("Invalid date");
      setWarning("");
      onValidation?.({ error: "Invalid date", warning: null });
    }
  }, [onChange, preset, assignedTaxYear, onValidation, warning]);

  const handleBlur = useCallback(() => {
    commit(text);
  }, [commit, text]);

  // Enter COMMITS the typed date and then still moves on. preventDefault stops
  // the browser submitting anything; the data-enter="advance" mark on the input
  // below tells the app-wide next-field rule that this keypress, unlike every
  // other one that has already been handled, should go on to advance. (ZEN-55)
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit(text);
    }
  }, [commit, text]);

  const handleCalendarSelect = useCallback((day) => {
    const iso = day ? dateToIso(day) : "";
    setError("");
    setText(iso ? fmtDate(iso, getDateFormat()) : "");
    lastEmitted.current = iso;
    onChange(iso);
    setTouched(true);
    setCalOpen(false);
    if (iso) {
      const v = validateDate(iso, { preset, assignedTaxYear });
      setError(v.error || "");
      setWarning(v.warning || "");
      if (v.warning !== warning) setWarningAcked(false);
      onValidation?.(v);
    }
  }, [onChange, preset, assignedTaxYear, onValidation, warning]);

  const handleToday = useCallback(() => {
    setError("");
    setText(fmtDate(TODAY_ISO, getDateFormat()));
    lastEmitted.current = TODAY_ISO;
    onChange(TODAY_ISO);
    setTouched(true);
    const v = validateDate(TODAY_ISO, { preset, assignedTaxYear });
    setError(v.error || "");
    setWarning(v.warning || "");
    onValidation?.(v);
  }, [onChange, preset, assignedTaxYear, onValidation]);

  const selected = useMemo(() => isoToDate(value), [value]);
  const fromDate = useMemo(() => isoToDate(min), [min]);
  const toDate = useMemo(() => isoToDate(max), [max]);
  const hint = getDateFormatHint();
  const isToday = value === TODAY_ISO;

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-center gap-1.5 w-full">
        <div className={`flex h-9 flex-1 items-center rounded-md border bg-white text-sm ${error ? "border-[#F01919] ring-1 ring-[#F01919]/30" : warning && !warningAcked ? "border-[#D97706] ring-1 ring-[#D97706]/30" : "border-[#C2C9D1] focus-within:border-[#034AA2] focus-within:ring-2 focus-within:ring-[#034AA2]/30"} ${disabled ? "cursor-not-allowed opacity-50" : ""}`}>
          <input
            ref={inputRef}
            type="text"
            inputMode="text"
            autoComplete="off"
            value={text}
            onChange={(e) => { setText(e.target.value); setError(""); setWarning(""); }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={placeholder || hint}
            data-enter="advance"
            data-testid={testid}
            className={`h-full w-full bg-transparent px-2 text-sm outline-none ${isTentative ? "text-[#9CA3AF]" : ""}`}
          />
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                tabIndex={-1}
                data-testid={testid ? `${testid}-cal` : undefined}
                className="shrink-0 px-1.5 text-[#6b7280] hover:text-[#034AA2]"
              >
                <CalendarBlank size={15} />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selected}
                onSelect={handleCalendarSelect}
                defaultMonth={selected || undefined}
                fromDate={fromDate}
                toDate={toDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        {!hideToday && !disabled && (
          <button
            type="button"
            onClick={handleToday}
            className="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold text-[#034AA2] bg-[#034AA2]/10 hover:bg-[#034AA2]/20 transition-colors whitespace-nowrap"
            tabIndex={-1}
            data-testid={testid ? `${testid}-today` : undefined}
            title="Set to today"
          >
            Today
          </button>
        )}
      </div>
      {error && (
        <p className="mt-0.5 text-[10px] text-[#F01919] font-medium leading-tight" data-testid={testid ? `${testid}-error` : undefined}>{error}</p>
      )}
      {!error && warning && !warningAcked && (
        <div className="mt-0.5 flex items-start gap-1 rounded border border-[#D97706]/30 bg-[#FEF3C7] px-2 py-1" data-testid={testid ? `${testid}-warning` : undefined}>
          <WarningCircle size={13} weight="fill" className="mt-px shrink-0 text-[#D97706]" />
          <span className="flex-1 text-[10px] text-[#92400E] leading-tight">{warning}</span>
          <button type="button" onClick={() => setWarningAcked(true)} className="shrink-0 text-[10px] font-bold text-[#92400E] underline hover:no-underline" data-testid={testid ? `${testid}-ack` : undefined}>OK</button>
        </div>
      )}
    </div>
  );
}
